'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import config from '@haaretz/l-config';
import useUser from '@haaretz/s-atoms/user';
import Button from '@haaretz/s-button';
import Card from '@haaretz/s-card';
import { site } from '@haaretz/s-consts';
import EmailField from '@haaretz/s-form-fields/EmailField';
import FirstNameField from '@haaretz/s-form-fields/FirstNameField';
import LastNameField from '@haaretz/s-form-fields/LastNameField';
import PhoneField from '@haaretz/s-form-fields/PhoneField';
import HtzLi from '@haaretz/s-htz-li';
import Icon from '@haaretz/s-icon';
import PpLandingPageLayout from '@haaretz/s-pp-landing-page-layout';
import RadioButton from '@haaretz/s-radio-button';
import RadioGroup from '@haaretz/s-radio-group';
import StyledModal from '@haaretz/s-styled-modal';
import TextLink from '@haaretz/s-text-link';
import useBi from '@haaretz/s-use-bi';
import React from 'react';
import s9 from 'style9';
import { AstronautImageError, AstronautImageSuccess } from './utils/Astronauts';
const c = s9.create({
    base: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        margin: 'auto',
        "@media all and (min-width:37.5em)": {
            maxWidth: '480px'
        },
        "@media all and (min-width:64em)": {
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            marginBottom: 0,
            maxWidth: '972px'
        }
    },
    title: {
        color: "rgb(var(--c-bgLightAllModes))",
        textWrap: 'balance',
        marginBottom: "2.5rem",
        marginTop: "2rem",
        fontSize: "var(--fz-9)",
        lineHeight: "var(--lh-9)",
        paddingRight: "0.75rem",
        paddingLeft: "0.75rem",
        "@media all and (min-width:64em)": {
            paddingRight: "7.75rem",
            paddingLeft: "7.75rem"
        },
        "@media all and (min-width:80em) and (max-width:101.9375em)": {
            fontSize: "var(--fz-10)",
            lineHeight: "var(--lh-10)"
        },
        "@media all and (min-width:102em)": {
            marginTop: "4.25rem",
            fontSize: "var(--fz-10)",
            lineHeight: "var(--lh-10)"
        }
    },
    detailsWrapper: {
        marginTop: "2rem",
        display: 'flex',
        flexDirection: 'column',
        rowGap: "1rem",
        textAlign: 'start',
        "@media all and (min-width:64em)": {
            rowGap: "1.75rem",
            marginTop: "2.5rem"
        }
    },
    innerFormTitle: {
        fontSize: "var(--fz-1)",
        lineHeight: "var(--lh-1)"
    },
    innerTitle: {
        textWrap: 'balance',
        marginBottom: "2rem",
        fontSize: "var(--fz-3)",
        lineHeight: "var(--lh-3)",
        "@media all and (min-width:64em)": {
            marginBottom: "2.5rem",
            fontSize: "var(--fz-6)",
            lineHeight: "var(--lh-6)"
        },
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz-5)",
            lineHeight: "var(--lh-5)"
        }
    },
    subscriptionTypeWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        rowGap: "1.25rem",
        "@media all and (min-width:64em)": {
            flexDirection: 'row',
            columnGap: "1.25rem",
            justifyContent: 'space-between'
        }
    },
    subscriptionTypeCard: {
        backgroundColor: "rgb(var(--c-cardGiftSubscription))",
        borderRadius: "clamp(0px, ((100vw - 4px) - 100%) * 9999, 0.75rem) / 0.75rem",
        paddingTop: "1.75rem",
        paddingBottom: "1.75rem",
        paddingRight: "0.75rem",
        paddingLeft: "0.75rem",
        minWidth: "14.25rem",
        transitionDuration: '0.2s',
        transitionProperty: 'background-color',
        transitionTimingFunction: 'ease-in-out',
        ':hover': {
            backgroundColor: "rgb(var(--c-cardGiftSubscriptionHover))"
        },
        ':focus': {
            backgroundColor: "rgb(var(--c-cardGiftSubscriptionHover))"
        },
        ':active': {
            backgroundColor: "rgb(var(--c-cardGiftSubscriptionHover))"
        }
    },
    selectedSubscriptionTypeCard: {
        borderBottomColor: "rgb(var(--c-primary800))",
        borderBottomWidth: "4px",
        borderBottomStyle: 'solid',
        paddingBottom: "calc(1.75rem - 4px)",
        borderTopColor: "rgb(var(--c-primary800))",
        borderTopWidth: "4px",
        borderTopStyle: 'solid',
        paddingTop: "calc(1.75rem - 4px)",
        borderInlineEndColor: "rgb(var(--c-primary800))",
        borderInlineEndWidth: "4px",
        borderInlineEndStyle: 'solid',
        paddingInlineEnd: "0.75rem",
        borderInlineStartColor: "rgb(var(--c-primary800))",
        borderInlineStartWidth: "4px",
        borderInlineStartStyle: 'solid',
        paddingInlineStart: "0.75rem"
    },
    subscriptionTypeText: {
        marginTop: "0.25rem",
        fontSize: "var(--fz-2)",
        lineHeight: "var(--lh-2)",
        "@media all and (min-width:64em)": {
            fontSize: "var(--fz-0)",
            lineHeight: "var(--lh-0)"
        }
    },
    bold: {
        fontWeight: 700
    },
    subscriptionTypeTitle: {
        fontSize: "var(--fz-4)",
        lineHeight: "var(--lh-4)",
        "@media all and (min-width:64em)": {
            fontSize: "var(--fz-2)",
            lineHeight: "var(--lh-2)"
        }
    },
    label: {
        fontSize: "var(--fz-0)",
        lineHeight: "var(--lh-0)",
        "@media all and (min-width:64em)": {
            fontSize: "var(--fz-0)",
            lineHeight: "var(--lh-0)"
        },
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz--1)",
            lineHeight: "var(--lh--1)"
        }
    },
    paymentWrapper: {
        textAlign: 'start',
        marginTop: "2rem",
        marginBottom: "2rem",
        "@media all and (min-width:64em)": {
            marginTop: "2.5rem",
            marginBottom: "2.5rem"
        }
    },
    paymentTitle: {
        marginBottom: "0.75rem",
        "@media all and (min-width:64em)": {
            marginBottom: "1.75rem"
        }
    },
    ppModal: {
        overflow: 'visible',
        padding: 0,
        "@media all and (min-width:64em)": {
            maxWidth: '540px'
        }
    },
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: "7rem",
        paddingBottom: "2.5rem",
        paddingRight: "1.5rem",
        paddingLeft: "1.5rem",
        textAlign: 'center',
        "@media all and (min-width:64em)": {
            paddingBottom: "8.5rem",
            paddingRight: "5rem",
            paddingLeft: "5rem"
        }
    },
    modalContentErrorHdc: {
        "@media all and (min-width:64em)": {
            paddingRight: "4rem",
            paddingLeft: "4rem"
        }
    },
    modalTitle: {
        marginBottom: "0.75rem",
        fontSize: "var(--fz-3)",
        lineHeight: "var(--lh-3)",
        "@media all and (min-width:64em)": {
            marginBottom: "1.25rem",
            fontSize: "var(--fz-2)",
            lineHeight: "var(--lh-2)"
        }
    },
    modalTitleSuccess: {
        marginBottom: "0.75rem",
        fontSize: "var(--fz-2)",
        lineHeight: "var(--lh-2)",
        "@media all and (min-width:64em)": {
            marginBottom: "1.25rem",
            fontSize: "var(--fz-4)",
            lineHeight: "var(--lh-4)"
        }
    },
    modalTitleErrorHdc: {
        fontSize: "var(--fz-1)",
        lineHeight: "var(--lh-1)",
        "@media all and (min-width:64em)": {
            fontSize: "var(--fz-1)",
            lineHeight: "var(--lh-1)"
        }
    },
    modalText: {
        fontSize: "var(--fz-1)",
        lineHeight: "var(--lh-1)"
    },
    modalContentSuccess: {
        paddingTop: "8.25rem",
        paddingBottom: "3rem",
        paddingRight: "3.75rem",
        paddingLeft: "3.75rem",
        "@media all and (min-width:64em)": {
            paddingBottom: "6.75rem",
            paddingRight: "3rem",
            paddingLeft: "3rem"
        }
    },
    modalTextSuccess: {
        fontSize: "var(--fz-0)",
        lineHeight: "var(--lh-0)",
        "@media all and (min-width:64em)": {
            fontSize: "var(--fz-1)",
            lineHeight: "var(--lh-1)",
            maxWidth: '400px'
        }
    },
    modalTextErrorHdc: {
        fontSize: "var(--fz-0)",
        lineHeight: "var(--lh-0)",
        "@media all and (min-width:64em)": {
            fontSize: "var(--fz-1)",
            lineHeight: "var(--lh-1)"
        }
    },
    warnIcon: {
        fontSize: "1.5rem",
        alignSelf: 'flex-start',
        verticalAlign: '-0.1em',
        marginInlineEnd: "0.25rem",
        "@media all and (min-width:64em)": {
            fontSize: "2rem"
        }
    },
    list: {
        textAlign: 'start',
        marginTop: "5rem",
        backdropFilter: '-moz-initial',
        fontSize: "var(--fz--1)",
        lineHeight: "1.5rem",
        "@media all and (min-width:64em)": {
            marginTop: "5rem"
        },
        "@media all and (min-width:64em) and (max-width:79.9375em)": {
            fontSize: "var(--fz-0)",
            lineHeight: "2rem"
        },
        "@media all and (min-width:80em) and (max-width:101.9375em)": {
            fontSize: "var(--fz--1)",
            lineHeight: "2rem"
        },
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz--3)",
            lineHeight: "1.25rem"
        }
    },
    giftSubTextHdc: {
        fontSize: "var(--fz-0)",
        lineHeight: "var(--lh-0)",
        "@media all and (min-width:80em) and (max-width:101.9375em)": {
            fontSize: "var(--fz--1)",
            lineHeight: "var(--lh--1)"
        },
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz--2)",
            lineHeight: "var(--lh--2)"
        }
    },
    radioBtnHdc: {
        columnGap: "0.75rem",
        alignItems: 'start'
    },
    radioBtnCircle: {
        marginTop: 'calc((1lh - 1em) / 2)'
    }
});
const inputGiverNames = {
    firstName: 'firstNameGiver',
    lastName: 'lastNameGiver',
    email: 'emailGiver',
    phone: 'phoneGiver'
};
const inputReceiverNames = {
    firstName: 'firstNameReceiver',
    lastName: 'lastNameReceiver',
    email: 'emailReceiver',
    phone: 'phoneReceiver'
};
const textByPeroidCalc = {
    3: '3 Month',
    6: '6 Month',
    12: '12 Month'
};
const priceByPeriodCalc = {
    3: 39,
    6: 59,
    12: 79
};
const saleCodeByPeriodCalc = {
    3: 3492,
    6: 3491,
    12: 3488
};
const cardTypes = {
    existingCard: 'existingCard',
    differentCard: 'differentCard'
};
const texts = {
    successExistCard: {
        title: 'Thank you!',
        text: 'Your details have been received, and a receipt will be sent to your email shortly.'
    },
    successDiffCard: {
        title: "Thank you! We're on it.",
        text: 'Our customer service team will get back to you shortly to continue the process.'
    },
    error: {
        title: 'Oops! Something Went Wrong.',
        text: 'Please try again, or contact our customer service team at'
    }
};
function SubscriptionType({ periodCalc, selectedPeriodCalc, setSelectedPeriodCalc }) {
    const currency = '$';
    return /*#__PURE__*/ _jsxs(Card, {
        as: "button",
        type: "button",
        styleExtend: [
            c.subscriptionTypeCard,
            periodCalc === selectedPeriodCalc && c.selectedSubscriptionTypeCard
        ],
        onClick: ()=>setSelectedPeriodCalc(periodCalc),
        children: [
            /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: s9(c.subscriptionTypeTitle),
                        children: textByPeroidCalc[periodCalc]
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: s9(c.subscriptionTypeText, ...[
                            c.giftSubTextHdc
                        ]),
                        children: "Gift Subscription"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: s9(c.subscriptionTypeText, ...[
                    c.bold
                ]),
                children: [
                    currency,
                    priceByPeriodCalc[periodCalc]
                ]
            })
        ]
    });
}
function Subscriptions({ selectedPeriodCalc, setSelectedPeriodCalc }) {
    const sharedProps = {
        selectedPeriodCalc,
        setSelectedPeriodCalc
    };
    const subscriptions = [
        {
            ...sharedProps,
            periodCalc: 3
        },
        {
            ...sharedProps,
            periodCalc: 6
        },
        {
            ...sharedProps,
            periodCalc: 12
        }
    ];
    return /*#__PURE__*/ _jsx("div", {
        className: s9(c.subscriptionTypeWrapper),
        children: subscriptions.map((item)=>{
            return /*#__PURE__*/ _jsx(SubscriptionType, {
                ...item
            }, item.periodCalc);
        })
    });
}
function GiverForm() {
    const { firstName, lastName, userMail } = useUser('cookieValue');
    return /*#__PURE__*/ _jsxs("div", {
        className: s9(c.detailsWrapper),
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: s9(c.innerFormTitle),
                children: 'My info (the gift-giver)'
            }),
            /*#__PURE__*/ _jsx(FirstNameField, {
                name: inputGiverNames.firstName,
                defaultValue: firstName
            }),
            /*#__PURE__*/ _jsx(LastNameField, {
                name: inputGiverNames.lastName,
                defaultValue: lastName
            }),
            /*#__PURE__*/ _jsx(EmailField, {
                name: inputGiverNames.email,
                defaultValue: userMail,
                autoFocus: false
            }),
            /*#__PURE__*/ _jsx(PhoneField, {
                name: inputGiverNames.phone
            })
        ]
    });
}
function ReceiverForm() {
    return /*#__PURE__*/ _jsxs("div", {
        className: s9(c.detailsWrapper),
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: s9(c.innerFormTitle),
                children: 'I would like to send a gift subscription to'
            }),
            /*#__PURE__*/ _jsx(FirstNameField, {
                name: inputReceiverNames.firstName
            }),
            /*#__PURE__*/ _jsx(LastNameField, {
                name: inputReceiverNames.lastName
            }),
            /*#__PURE__*/ _jsx(EmailField, {
                name: inputReceiverNames.email,
                autoFocus: false
            }),
            /*#__PURE__*/ _jsx(PhoneField, {
                name: inputReceiverNames.phone
            })
        ]
    });
}
function PaymentOptions() {
    const sharedProps = {
        styleExtend: [
            c.radioBtnHdc
        ],
        textStyleExtend: [
            c.label
        ],
        circleStyleExtend: [
            c.radioBtnCircle
        ]
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: s9(c.paymentWrapper),
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: s9(c.innerFormTitle, c.paymentTitle),
                children: 'How do you want to pay?'
            }),
            /*#__PURE__*/ _jsxs(RadioGroup, {
                defaultValue: cardTypes.existingCard,
                groupName: "card",
                children: [
                    /*#__PURE__*/ _jsx(RadioButton, {
                        value: cardTypes.existingCard,
                        ...sharedProps,
                        children: 'With the credit card I use for my personal subscription (*If your subscription is paid via PayPal or Apple, Haaretz will contact you to collect credit card details)'
                    }),
                    /*#__PURE__*/ _jsx(RadioButton, {
                        value: cardTypes.differentCard,
                        ...sharedProps,
                        children: 'With a different credit card (*Gift subscriptions must be paid for using a credit card)'
                    })
                ]
            })
        ]
    });
}
function ListItem({ text, isLastItem }) {
    return /*#__PURE__*/ _jsxs(HtzLi, {
        variant: "neutral",
        children: [
            null,
            text
        ]
    });
}
const listTexts = [
    'This subscription has been purchased as a gift and will not be used by the purchaser.',
    'This subscription is for a limited period, based on the duration purchased, and will not automatically renew at the end of the period.',
    'The gift recipient will receive prior notification before the subscription period ends and will have the option to purchase a subscription for themselves.',
    "The website's terms of use will apply to both the purchaser and the gift recipient."
];
function ResultModal({ modalType, setModalType }) {
    const isSuccess = modalType.includes('success');
    const isError = modalType === 'error';
    return /*#__PURE__*/ _jsx(StyledModal, {
        styleExtend: [
            c.ppModal
        ],
        isOpen: modalType !== 'closed',
        closeBtnOnClick: ()=>setModalType('closed'),
        onToggle: (isOpen)=>isOpen ? undefined : setModalType('closed'),
        labelledBy: modalType === 'closed' ? null : texts[modalType]?.title,
        children: modalType === 'closed' ? null : /*#__PURE__*/ _jsxs("div", {
            className: s9(c.modalContent, isSuccess && c.modalContentSuccess, isError && c.modalContentErrorHdc),
            children: [
                isSuccess ? /*#__PURE__*/ _jsx(AstronautImageSuccess, {}) : /*#__PURE__*/ _jsx(AstronautImageError, {}),
                /*#__PURE__*/ _jsxs("h2", {
                    className: s9(isSuccess ? c.modalTitleSuccess : c.modalTitle, isError && c.modalTitleErrorHdc),
                    children: [
                        isError && /*#__PURE__*/ _jsx(Icon, {
                            icon: "warn",
                            variant: "danger",
                            styleExtend: [
                                c.warnIcon
                            ]
                        }),
                        texts[modalType].title
                    ]
                }),
                /*#__PURE__*/ _jsxs("p", {
                    className: s9(isSuccess ? c.modalTextSuccess : c.modalText, isError && c.modalTextErrorHdc),
                    children: [
                        texts[modalType].text,
                        isError ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsx(TextLink, {
                                    href: "https://www.haaretz.co.il/misc/terms-of-use",
                                    children: "digital-english@haaretz.co.il"
                                })
                            ]
                        }) : null
                    ]
                })
            ]
        })
    });
}
const msPurchase = config.get('msPurchase');
export default function PpGiftSubscriptionPage() {
    const [selectedPeriodCalc, setSelectedPeriodCalc] = React.useState(12);
    const [modalType, setModalType] = React.useState('closed');
    const biAction = useBi();
    const [isLoading, setIsLoading] = React.useState(false);
    const onSubmit = async (event)=>{
        event.preventDefault();
        const inputs = {
            firstNameGiver: event.currentTarget.elements.namedItem('firstNameGiver'),
            lastNameGiver: event.currentTarget.elements.namedItem('lastNameGiver'),
            emailGiver: event.currentTarget.elements.namedItem('emailGiver'),
            phoneGiver: event.currentTarget.elements.namedItem('phoneGiver'),
            firstNameReceiver: event.currentTarget.elements.namedItem('firstNameReceiver'),
            lastNameReceiver: event.currentTarget.elements.namedItem('lastNameReceiver'),
            emailReceiver: event.currentTarget.elements.namedItem('emailReceiver'),
            phoneReceiver: event.currentTarget.elements.namedItem('phoneReceiver')
        };
        const cardOptions = event.currentTarget.elements.namedItem('card');
        let selectedCardValue;
        if (cardOptions) {
            const seleceted = Array.from(cardOptions).find((option)=>{
                return option.checked;
            });
            const selecetedValue = seleceted.value;
            if (selecetedValue === cardTypes.existingCard || selecetedValue === cardTypes.differentCard) {
                selectedCardValue = selecetedValue;
            }
        }
        if (inputs.firstNameGiver && inputs.lastNameGiver && inputs.emailGiver && inputs.phoneGiver && inputs.firstNameReceiver && inputs.lastNameReceiver && inputs.emailReceiver && inputs.phoneReceiver && selectedCardValue) {
            setIsLoading(true);
            const firstNameGiverValue = inputs.firstNameGiver.value.trim();
            const lastNameGiverValue = inputs.lastNameGiver.value.trim();
            const emailGiverValue = inputs.emailGiver.value.trim();
            const phoneGiverValue = inputs.phoneGiver.value.trim();
            const firstNameReceiverValue = inputs.firstNameReceiver.value.trim();
            const lastNameReceiverValue = inputs.lastNameReceiver.value.trim();
            const emailReceiverValue = inputs.emailReceiver.value.trim();
            const phoneReceiverValue = inputs.phoneReceiver.value.trim();
            const formData = {
                gift_type: selectedPeriodCalc.toString(),
                giver_firstname: firstNameGiverValue,
                giver_lastname: lastNameGiverValue,
                giver_mail: emailGiverValue,
                giver_phone: phoneGiverValue,
                reciver_firstname: firstNameReceiverValue,
                reciver_lastname: lastNameReceiverValue,
                reciver_mail: emailReceiverValue,
                reciver_phone: phoneReceiverValue,
                payment_method: selectedCardValue === cardTypes.existingCard ? 'existing card' : 'different card',
                site
            };
            biAction({
                feature: 'gift landing page',
                feature_type: 'Marketing',
                campaign_name: 'gift subscription',
                campaign_details: selectedCardValue === cardTypes.existingCard ? 'my card' : 'other card',
                newsletter_email: emailReceiverValue,
                action_id: 189,
                sale_code: saleCodeByPeriodCalc[selectedPeriodCalc],
                additional_info: formData
            });
            try {
                const res = await fetch(`${msPurchase}/helper/gift`, {
                    method: 'POST',
                    credentials: 'include',
                    body: JSON.stringify(formData)
                });
                const data = await res.json();
                if (data.status === 'success') {
                    setModalType(selectedCardValue === cardTypes.existingCard ? 'successExistCard' : 'successDiffCard');
                } else {
                    setModalType('error');
                }
            } catch (error) {
                setModalType('error');
            } finally{
                setIsLoading(false);
            }
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(PpLandingPageLayout, {
                title: 'Share the Gift of Haaretz',
                innerTitle: 'Subscribers can now purchase a gift subscription at a special discounted rate',
                styleExtend: [
                    c.base
                ],
                titleStyleExtend: [
                    c.title
                ],
                innerTitleStyleExtend: [
                    c.innerTitle
                ],
                children: [
                    /*#__PURE__*/ _jsxs("form", {
                        onSubmit: onSubmit,
                        children: [
                            /*#__PURE__*/ _jsx(Subscriptions, {
                                selectedPeriodCalc: selectedPeriodCalc,
                                setSelectedPeriodCalc: setSelectedPeriodCalc
                            }),
                            /*#__PURE__*/ _jsx(GiverForm, {}),
                            /*#__PURE__*/ _jsx(ReceiverForm, {}),
                            /*#__PURE__*/ _jsx(PaymentOptions, {}),
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "sales",
                                type: "submit",
                                ...isLoading ? {
                                    state: 'busy',
                                    busyNotice: 'loading...'
                                } : {
                                    state: 'auto'
                                },
                                children: 'Buy Now'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: s9(c.list),
                        children: listTexts.map((text, i)=>{
                            return text ? /*#__PURE__*/ _jsx(ListItem, {
                                text: text,
                                isLastItem: i + 1 === listTexts.length
                            }, text) : null;
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ResultModal, {
                modalType: modalType,
                setModalType: setModalType
            })
        ]
    });
}
const colorCssVariablesHtz = [
    "bgLightAllModes",
    "cardGiftSubscription",
    "cardGiftSubscriptionHover",
    "primary800"
];
const lineHeightCssVariablesHtz = [
    "--lh-9",
    "--lh-10",
    "--lh-1",
    "--lh-3",
    "--lh-6",
    "--lh-5",
    "--lh-2",
    "--lh-0",
    "--lh-4",
    "--lh--1",
    "--lh--2"
];
const fontSizeCssVariablesHtz = [
    "--fz-9",
    "--fz-10",
    "--fz-1",
    "--fz-3",
    "--fz-6",
    "--fz-5",
    "--fz-2",
    "--fz-0",
    "--fz-4",
    "--fz--1",
    "--fz--3",
    "--fz--2"
];

//# sourceMappingURL=PpGiftSubscriptionPage.page.js.map